import React from 'react';
import type { CSSProperties } from 'react';
import { hasBlockPadding, type BlockPaddingFields } from './blockBackgroundStyle';

/**
 * WEB-1680 — РАССТОЯНИЕ МЕЖДУ БЛОКАМИ: отступ снизу и сверху у каждого блока,
 * с возвратом к умолчанию своего типа.
 *
 * Как было: расстояние зашито числом в КАЖДУЮ ветку рендера — `marginBottom`
 * встречается в обоих рендерерах больше двадцати раз, у заголовка 16, у
 * подзаголовка 12, у разделителя нет вовсе. Настройки не было, автор изменить
 * это не мог ничем.
 *
 * ОДНА ТОЧКА ПРАВДЫ. Умолчания собраны здесь и отсюда читаются обоими
 * рендерерами. Справочник не выдуман: он ВЫВЕДЕН ИЗ РАЗМЕТКИ — сторож
 * `__tests__/blockSpacing.web1680.test.tsx` рендерит каждый тип из union
 * `BlockType`, читает `margin-bottom` корневого узла и сверяет с этим файлом.
 * Появится в рендере новое зашитое число в обход справочника — сторож
 * покраснеет и назовёт тип.
 *
 * Значения хранятся исключениями поверх общего умолчания, а не таблицей из
 * восьмидесяти строк: таблица, где семьдесят пять строк равны 16, разъезжается
 * молча — забытая строка выглядит в ней ровно как заполненная.
 *
 * ПОЧЕМУ ОБЁРТКА, А НЕ ПРАВКА КОРНЯ. Первым заходом отступ подменялся прямо на
 * корневом узле блока (`cloneElement`) — но у восемнадцати типов корень это
 * собственный компонент (тизер, билет промо, шкала, попап скидки и другие),
 * он `style` наружу не отдаёт, и настройка до них не доезжала. Это ровно класс
 * «одна дверь из двух», которого карточка требует избежать. Поэтому отступ
 * несёт обёртка, одинаковая для всех типов, а прежнее зашитое число гасит
 * правило на `[data-block-spacing]` в global.css — иначе уменьшить
 * расстояние было бы нельзя: поля не складываются, а схлопываются в максимум.
 *
 * ГДЕ СТОИТ ОБЁРТКА (WEB-1708). СНАРУЖИ обёртки подложки («Фон блока»), а не
 * внутри неё: внутри коробки с ненулевым внутренним отступом вертикальные поля
 * наружу не выходят — зазора до соседних блоков не появляется вовсе, растёт
 * только пустота внутри карточки. Из-за этого же правило гашения перечисляет
 * не только прямого ребёнка, но и путь сквозь подложку: корень блока с
 * заливкой лежит на уровень глубже.
 *
 * СХЛОПЫВАНИЕ ПОЛЕЙ (дополнение владельца). Отступ сверху у этого блока и
 * отступ снизу у предыдущего — обычные вертикальные поля в потоке, и по
 * правилам CSS они СХЛОПЫВАЮТСЯ: видимый зазор между двумя блоками равен
 * максимуму из двух, а не сумме. Это не дефект: автор просит «не меньше N», и
 * получает ровно это. Поведение закреплено тестом, чтобы следующая правка не
 * приняла его за баг и не начала «чинить» суммой.
 */

/** Общее умолчание: столько стоит у подавляющего большинства типов. */
export const DEFAULT_BLOCK_SPACING_BOTTOM = 16;

/**
 * Исключения из общего умолчания — ровно те числа, что стоят в рендере.
 * Ноль = отступа снизу у типа нет вовсе (распорка со своей высотой, экран
 * результата).
 *
 * WEB-1709 (расширенный сторож, 01.09): у разделителя здесь стоял ноль, а в
 * рендере — `margin: '16px auto'` на самом `<hr>` (dividerLineStyle.ts).
 * Прежний сторож читал число регуляркой по `margin-bottom:` и СОКРАЩЁННУЮ
 * запись `margin` не видел вовсе — расхождение держалось зелёным. Цена вранья
 * была не косметическая: стоило автору задать разделителю отступ СВЕРХУ, как
 * незаданная нижняя сторона бралась отсюда нулём, зашитые 16 гасило правило, и
 * нижний зазор молча пропадал. Число приведено к тому, что в рендере.
 */
export const BLOCK_SPACING_BOTTOM_OVERRIDES: Readonly<Record<string, number>> = {
  sub_title: 12,
  paragraph: 12,
  heading: 12,
  progress_text: 12,
  restore_purchases: 12,
  result_screen: 0,
  spacer: 0,
};

/** Умолчание отступа снизу для типа блока — «пусто» в панели равно ему. */
export function defaultSpacingBottom(blockType: string): number {
  return BLOCK_SPACING_BOTTOM_OVERRIDES[blockType] ?? DEFAULT_BLOCK_SPACING_BOTTOM;
}

/** Сверху отступа не было ни у одного типа — умолчание ноль. */
export const DEFAULT_BLOCK_SPACING_TOP = 0;

export interface BlockSpacingFields extends BlockPaddingFields {
  type: string;
  /** WEB-1680 — отступ снизу, px. Absent = умолчание своего типа блока. */
  spacingBottom?: number;
  /** WEB-1680 — отступ сверху, px. Absent = как раньше: сверху отступа нет. */
  spacingTop?: number;
}

/**
 * WEB-1709 (живая проверка владельца 01.09) — ПОЛ, КОТОРЫЙ ДЕРЖАЛ СОСЕД.
 *
 * Автор ставил картинке расстояние 0, а видел 16. Замер настоящим браузером на
 * разметке этих же рендереров: зашитое число САМОЙ картинки гасится правильно
 * (0px и с подписью, и без, и в конструкторе, и на публичной странице), а
 * зазор держал СЛЕДУЮЩИЙ блок. У «Абзаца» корень — `<p>`, которому верхнее
 * поле 1em (16px при кегле 16) даёт сама таблица браузера; своего `margin-top`
 * рендер не ставит, а правил, обнуляющих поля абзацев и заголовков, в проекте
 * нет (это прямо сказано в комментарии WEB-1678 в global.css). Поля соседей
 * СХЛОПЫВАЮТСЯ В МАКСИМУМ, отсюда max(заданное, 16). Различитель: с
 * соседом-кнопкой (у неё такого поля нет) те же значения дают ровно 0/8/32.
 *
 * Обнулить эти поля поголовно нельзя — подвинулись бы живые воронки, где автор
 * расстоянием не занимался («абзац после абзаца» стал бы 12 вместо 16).
 * Поэтому обёртку расстояния получает и СЛЕДУЮЩИЙ блок — но только когда у
 * предыдущего расстояние снизу задано ЯВНО: тогда правило
 * `[data-block-spacing] > *` гасит верхнее поле его корня, обёртка несёт
 * `margin-top: 0`, и зазор равен ровно тому, что задал автор.
 *
 * ПОЧЕМУ СМОТРИМ ТОЛЬКО НИЖНЮЮ СТОРОНУ ПРЕДЫДУЩЕГО. Зазором A→B распоряжается
 * отступ СНИЗУ у A. Тронул автор у A только верх — про этот зазор он ничего не
 * говорил, и двигать его нельзя.
 *
 * СПИСОК — ОТРИСОВАННЫЕ СОСЕДИ. Передавать сюда нужно тот же список, который
 * экран реально печатает (видимые блоки), иначе «предыдущим» окажется блок,
 * которого на экране нет.
 */
export function blocksFollowingExplicitSpacing(
  blocks: ReadonlyArray<{ id: string; spacingBottom?: number }>,
): Set<string> {
  const ids = new Set<string>();
  for (let i = 1; i < blocks.length; i += 1) {
    if (typeof blocks[i - 1]?.spacingBottom === 'number') ids.add(blocks[i].id);
  }
  return ids;
}

/** Тронул ли автор расстояние. Нет → разметка прежняя, без лишнего узла. */
export function hasBlockSpacing(fields?: {
  spacingBottom?: number;
  spacingTop?: number;
}): boolean {
  return (
    typeof fields?.spacingBottom === 'number' ||
    typeof fields?.spacingTop === 'number'
  );
}

/**
 * WEB-1678 (возврат из приёмки 24.08) — ПОЧЕМУ ВНУТРЕННИЕ ОТСТУПЫ ПОДНИМАЮТ
 * ЭТУ ЖЕ ОБЁРТКУ.
 *
 * Зашитое число расстояния стоит на КОРНЕ блока, а корень лежит ВНУТРИ коробки
 * «Фона блока», у которой автор задаёт внутренние отступы. Поля наружу оттуда
 * не выходят (у коробки свой внутренний отступ), поэтому число превращается в
 * пустоту ВНУТРИ заливки: автор ставит нижний отступ 1 или 0, а под
 * содержимым всё равно полоса в 16px. Ровно это владелец и увидел на блоках
 * выбора веса и роста — их рисует собственный компонент `UnitPickerBlock`, но
 * дело не в нём: зашитое число несёт корень ЛЮБОГО типа (справочник выше).
 *
 * Лечится не правкой компонентов, а переездом числа на уровень, которым
 * управляют настройки: как только автор занялся внутренними отступами,
 * появляется эта обёртка, зашитое число корня гасит уже действующее правило
 * `[data-block-spacing] > [data-block-padding] > *` в global.css, а само
 * расстояние несёт обёртка — и равно оно умолчанию СВОЕГО типа, то есть тому
 * же числу, что стояло раньше. Вид воронки снаружи не меняется, внутри заливки
 * лишняя полоса исчезает. Правило внутренних отступов (`[data-block-padding]`)
 * при этом не трогали — оно про верхнее поле от таблицы браузера.
 *
 * Отступов не задавали → обёртки нет, правило спит, старая воронка не
 * двигается ни на пиксель.
 */

/** Итоговые отступы блока: своё значение, иначе умолчание своего типа. */
export function resolveBlockSpacing(fields: BlockSpacingFields): CSSProperties {
  return {
    marginTop: fields.spacingTop ?? DEFAULT_BLOCK_SPACING_TOP,
    marginBottom: fields.spacingBottom ?? defaultSpacingBottom(fields.type),
  };
}

/**
 * `followsExplicitSpacing` (WEB-1709) — блок идёт сразу за блоком, которому
 * автор задал расстояние снизу. Тогда обёртка ставится и без собственной
 * настройки: сверху она несёт умолчание верхней стороны (ноль), снизу —
 * умолчание СВОЕГО типа, то есть ровно то число, что зашито в корень и гасится
 * правилом. Значит расстояние до СЛЕДУЮЩЕГО блока не меняется, а пропадает
 * только неявное верхнее поле от таблицы браузера.
 */
export interface BlockSpacingOptions {
  followsExplicitSpacing?: boolean;
}

export function withBlockSpacing(
  fields: BlockSpacingFields,
  node: React.ReactElement | null,
  opts?: BlockSpacingOptions,
): React.ReactElement | null;
export function withBlockSpacing(
  fields: BlockSpacingFields,
  node: React.ReactNode,
  opts?: BlockSpacingOptions,
): React.ReactNode;
export function withBlockSpacing(
  fields: BlockSpacingFields,
  node: React.ReactNode,
  opts?: BlockSpacingOptions,
): React.ReactNode {
  if (
    !hasBlockSpacing(fields) &&
    !hasBlockPadding(fields) &&
    !opts?.followsExplicitSpacing
  ) {
    return node;
  }
  if (node === null || node === undefined || node === false) return node;
  return (
    <div data-block-spacing="1" style={resolveBlockSpacing(fields)}>
      {node}
    </div>
  );
}
